import { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getHizmetBySlug, getRelatedHizmetler, hizmetData } from '@/mocks/hizmet-data';

export default function HizmetLandingPage() {
  const { hizmetSlug } = useParams<{ hizmetSlug: string }>();
  const hizmet = getHizmetBySlug(hizmetSlug ?? '');
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    if (!hizmet) return;

    document.title = hizmet.metaTitle;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', hizmet.metaDesc);

    const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://example.com';

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `${siteUrl}/hizmetler/${hizmet.slug}`;

    const existingScript = document.getElementById('schema-hizmet-landing');
    if (existingScript) existingScript.remove();

    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Service',
          '@id': `${siteUrl}/hizmetler/${hizmet.slug}#service`,
          name: hizmet.name,
          description: hizmet.metaDesc,
          provider: {
            '@type': 'LocalBusiness',
            name: 'Alfa \u00c7ilingir',
            telephone: '+905416247982',
            address: {
              '@type': 'PostalAddress',
              addressLocality: 'Beylikd\u00fcz\u00fc',
              addressRegion: '\u0130stanbul',
              addressCountry: 'TR',
            },
          },
          areaServed: ['Beylikd\u00fcz\u00fc', 'Esenyurt', 'B\u00fcy\u00fck\u00e7ekmece', 'Bah\u00e7e\u015fehir'],
          availableChannel: {
            '@type': 'ServiceChannel',
            servicePhone: '+905416247982',
            availableLanguage: 'Turkish',
          },
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: siteUrl },
            { '@type': 'ListItem', position: 2, name: 'Hizmetler', item: `${siteUrl}/hizmetler` },
            { '@type': 'ListItem', position: 3, name: hizmet.name, item: `${siteUrl}/hizmetler/${hizmet.slug}` },
          ],
        },
      ],
    };

    const script = document.createElement('script');
    script.id = 'schema-hizmet-landing';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    // Separate FAQPage schema — avoids Google "repeated" warning
    const existingFaqScript = document.getElementById('schema-hizmet-faq');
    if (existingFaqScript) existingFaqScript.remove();

    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: hizmet.faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.a,
        },
      })),
    };

    const faqScript = document.createElement('script');
    faqScript.id = 'schema-hizmet-faq';
    faqScript.type = 'application/ld+json';
    faqScript.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(faqScript);

    return () => {
      const s = document.getElementById('schema-hizmet-landing');
      if (s) s.remove();
      const f = document.getElementById('schema-hizmet-faq');
      if (f) f.remove();
      const c = document.querySelector('link[rel="canonical"]');
      if (c) c.remove();
    };
  }, [hizmet]);

  if (!hizmet) return <Navigate to="/hizmetler" replace />;

  const related = getRelatedHizmetler(hizmet.relatedServices);

  return (
    <>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative min-h-[480px] flex items-end pb-0 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={hizmet.img}
              alt={`${hizmet.name} - Alfa Çilingir`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/88 via-black/65 to-black/25"></div>
          </div>
          <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 pt-32 pb-14">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-xs text-white/60 mb-5 flex-wrap">
              <Link to="/" className="hover:text-white cursor-pointer">Ana Sayfa</Link>
              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-right-s-line"></i></span>
              <Link to="/hizmetler" className="hover:text-white cursor-pointer">Hizmetler</Link>
              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-right-s-line"></i></span>
              <span className="text-white">{hizmet.name}</span>
            </nav>

            <div className="inline-flex items-center gap-2 border border-orange-400/60 text-orange-300 text-xs font-bold px-4 py-2 rounded-full mb-5 backdrop-blur-sm bg-white/5">
              <span className="w-2 h-2 bg-orange-400 rounded-full animate-ping absolute"></span>
              <span className="w-2 h-2 bg-orange-400 rounded-full relative"></span>
              7/24 PROFESYONEL HİZMET
            </div>

            <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-4 leading-tight">
              <span className="text-orange-400">{hizmet.name}</span>
            </h1>
            <p className="text-gray-200 text-lg md:text-xl mb-8 max-w-xl">
              {hizmet.heroDesc}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="tel:05416247982"
                className="inline-flex items-center justify-center gap-3 bg-orange-500 hover:bg-orange-400 text-white font-extrabold px-8 py-4 rounded-full whitespace-nowrap transition-all duration-200 cursor-pointer text-lg shadow-lg shadow-orange-500/30 animate-pulse"
              >
                <span className="w-6 h-6 flex items-center justify-center"><i className="ri-phone-fill text-xl"></i></span>
                Şimdi Ara — 0541 624 79 82
              </a>
              <a
                href="https://wa.me/905416247982"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-semibold px-7 py-4 rounded-full whitespace-nowrap transition-all duration-200 cursor-pointer text-base"
              >
                <span className="w-5 h-5 flex items-center justify-center"><i className="ri-whatsapp-line text-lg"></i></span>
                WhatsApp
              </a>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-orange-500 py-5">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              {[
                { val: '15 Dk', lbl: 'Ortalama Varış' },
                { val: '7/24', lbl: 'Kesintisiz Hizmet' },
                { val: '5000+', lbl: 'Mutlu Müşteri' },
                { val: '10+', lbl: 'Yıllık Deneyim' },
              ].map((s) => (
                <div key={s.lbl}>
                  <p className="text-2xl font-extrabold text-white">{s.val}</p>
                  <p className="text-orange-100 text-xs">{s.lbl}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16 md:py-20 bg-stone-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex flex-col lg:flex-row gap-12">
              {/* Left: About + Details */}
              <div className="lg:w-3/5">
                <span className="inline-flex items-center gap-2 text-orange-500 text-xs font-bold uppercase tracking-widest mb-3">
                  <span className="w-4 h-4 flex items-center justify-center"><i className={hizmet.icon}></i></span>
                  {hizmet.name}
                </span>
                <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-4">
                  Profesyonel {hizmet.name} Hizmeti
                </h2>
                <p className="text-gray-500 text-sm leading-relaxed mb-6">{hizmet.about}</p>

                {/* Details Grid */}
                <h3 className="font-bold text-gray-800 text-sm uppercase tracking-wider mb-4">Hizmet Kapsamı</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                  {hizmet.details.map((d) => (
                    <div key={d} className="flex items-center gap-2 bg-white rounded-xl px-4 py-3 border border-stone-100">
                      <span className="w-5 h-5 flex items-center justify-center text-orange-500 flex-shrink-0">
                        <i className="ri-check-double-line text-base"></i>
                      </span>
                      <span className="text-gray-700 text-sm font-medium">{d}</span>
                    </div>
                  ))}
                </div>

                {/* Steps */}
                <h3 className="font-bold text-gray-800 text-sm uppercase tracking-wider mb-4">Nasıl Çalışır?</h3>
                <div className="space-y-3">
                  {hizmet.steps.map((step, i) => (
                    <div key={step.title} className="flex items-start gap-4 bg-white rounded-xl p-4 border border-stone-100">
                      <div className="w-9 h-9 flex items-center justify-center bg-orange-500 text-white rounded-full font-extrabold text-sm flex-shrink-0">
                        {i + 1}
                      </div>
                      <div>
                        <p className="font-bold text-gray-900 text-sm">{step.title}</p>
                        <p className="text-gray-400 text-xs mt-0.5">{step.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right: CTA Card */}
              <div className="lg:w-2/5">
                <div className="bg-white rounded-2xl border border-stone-100 p-6 sticky top-24">
                  <div className="text-center mb-5">
                    <span className="w-16 h-16 flex items-center justify-center bg-orange-50 text-orange-500 rounded-2xl mx-auto mb-3">
                      <i className={`${hizmet.icon} text-3xl`}></i>
                    </span>
                    <h3 className="font-extrabold text-gray-900 text-lg">{hizmet.name} İçin Arayın</h3>
                    <p className="text-gray-400 text-sm mt-1">7/24 profesyonel hizmet</p>
                  </div>
                  <a
                    href="tel:05416247982"
                    className="flex items-center justify-center gap-3 bg-orange-500 hover:bg-orange-600 text-white font-extrabold py-4 rounded-xl whitespace-nowrap cursor-pointer transition-all duration-200 text-lg w-full mb-3"
                  >
                    <span className="w-6 h-6 flex items-center justify-center"><i className="ri-phone-fill"></i></span>
                    Şimdi Ara — 0541 624 79 82
                  </a>
                  <a
                    href="https://wa.me/905416247982"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="flex items-center justify-center gap-3 bg-green-500 hover:bg-green-600 text-white font-bold py-3 rounded-xl whitespace-nowrap cursor-pointer transition-all duration-200 text-sm w-full mb-5"
                  >
                    <span className="w-5 h-5 flex items-center justify-center"><i className="ri-whatsapp-line text-base"></i></span>
                    WhatsApp ile Yazış
                  </a>

                  <div className="space-y-3 text-sm">
                    {[
                      { icon: 'ri-timer-flash-line', text: 'Ortalama <strong>15 dakika</strong> içinde yanınızda' },
                      { icon: 'ri-24-hours-line', text: '<strong>7/24</strong> kesintisiz hizmet' },
                      { icon: 'ri-shield-check-line', text: 'Güvenilir ve deneyimli çilingirler' },
                      { icon: 'ri-price-tag-3-line', text: 'Şeffaf fiyat, gizli ücret yok' },
                    ].map((item) => (
                      <div key={item.icon} className="flex items-center gap-3 text-gray-600">
                        <span className="w-8 h-8 flex items-center justify-center bg-orange-50 text-orange-500 rounded-lg flex-shrink-0">
                          <i className={item.icon}></i>
                        </span>
                        <span dangerouslySetInnerHTML={{ __html: item.text }} />
                      </div>
                    ))}
                  </div>

                  {/* Service Areas */}
                  <div className="border-t border-stone-100 mt-5 pt-4">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Hizmet Bölgeleri</p>
                    <div className="flex flex-wrap gap-1.5">
                      {['Beylikdüzü', 'Esenyurt', 'Büyükçekmece', 'Bahçeşehir'].map((b) => (
                        <span key={b} className="bg-orange-50 text-orange-600 text-xs px-2.5 py-1 rounded-full border border-orange-100">
                          {b}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            <div className="text-center mb-10">
              <span className="inline-flex items-center gap-2 text-orange-500 text-xs font-bold uppercase tracking-widest mb-3">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-question-line"></i></span>
                Sık Sorulan Sorular
              </span>
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">
                {hizmet.name} Hakkında Merak Edilenler
              </h2>
            </div>
            <div className="space-y-3">
              {hizmet.faqs.map((faq, i) => (
                <div key={i} className="border border-stone-100 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-stone-50 transition-colors"
                  >
                    <span className="font-semibold text-gray-900 text-sm pr-4">{faq.q}</span>
                    <span className={`w-6 h-6 flex items-center justify-center text-orange-500 flex-shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-xl"></i>
                    </span>
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-4 text-gray-500 text-sm leading-relaxed border-t border-stone-50">
                      <p className="pt-3">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Services */}
        <section className="py-14 bg-stone-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <h2 className="text-xl font-extrabold text-gray-900 mb-6 text-center">Diğer Hizmetlerimiz</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {related.map((rel) => (
                <Link
                  key={rel.slug}
                  to={`/hizmetler/${rel.slug}`}
                  className="group bg-white rounded-2xl p-5 border border-stone-100 hover:border-orange-200 transition-all duration-200 cursor-pointer flex items-start gap-4"
                >
                  <span className="w-12 h-12 flex items-center justify-center bg-orange-50 text-orange-500 rounded-xl flex-shrink-0 group-hover:bg-orange-500 group-hover:text-white transition-all duration-200">
                    <i className={`${rel.icon} text-xl`}></i>
                  </span>
                  <div>
                    <p className="font-bold text-gray-900 text-sm group-hover:text-orange-600 transition-colors">{rel.name}</p>
                    <p className="text-gray-400 text-xs mt-1 line-clamp-2">{rel.heroDesc}</p>
                  </div>
                </Link>
              ))}
            </div>

            {/* All Services Link */}
            <div className="text-center mt-8">
              <Link
                to="/hizmetler"
                className="inline-flex items-center gap-2 border-2 border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white font-bold px-6 py-3 rounded-full whitespace-nowrap transition-all duration-200 cursor-pointer text-sm"
              >
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-list-check-2"></i></span>
                Tüm Hizmetleri Gör
              </Link>
            </div>
          </div>
        </section>

        {/* All Services Grid */}
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <h2 className="text-lg font-extrabold text-gray-900 mb-5 text-center">Tüm Çilingir Hizmetlerimiz</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
              {hizmetData.map((h) => (
                <Link
                  key={h.slug}
                  to={`/hizmetler/${h.slug}`}
                  className={`group flex flex-col items-center gap-2 p-4 rounded-xl border transition-all duration-200 cursor-pointer text-center ${
                    h.slug === hizmet.slug
                      ? 'bg-orange-500 border-orange-500 text-white'
                      : 'bg-stone-50 border-stone-100 hover:border-orange-200 hover:bg-orange-50'
                  }`}
                >
                  <span className={`w-10 h-10 flex items-center justify-center rounded-lg ${h.slug === hizmet.slug ? 'bg-white/20' : 'bg-white text-orange-500'}`}>
                    <i className={`${h.icon} text-lg ${h.slug === hizmet.slug ? 'text-white' : ''}`}></i>
                  </span>
                  <span className={`text-xs font-semibold leading-tight ${h.slug === hizmet.slug ? 'text-white' : 'text-gray-700 group-hover:text-orange-600'}`}>
                    {h.shortName}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-orange-500">
          <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
            <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-3">
              {hizmet.name} İçin Hemen Arayın
            </h2>
            <p className="text-orange-100 mb-6">7/24 hizmetinizdeyiz. 15 dakikada yanınızda olalım.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="tel:05416247982"
                className="inline-flex items-center gap-3 bg-white text-orange-500 font-extrabold px-10 py-5 rounded-full whitespace-nowrap cursor-pointer hover:bg-orange-50 transition-all duration-200 text-xl"
              >
                <span className="w-7 h-7 flex items-center justify-center"><i className="ri-phone-fill text-2xl"></i></span>
                Şimdi Ara — 0541 624 79 82
              </a>
              <a
                href="https://wa.me/905416247982"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center gap-3 bg-green-500 hover:bg-green-600 text-white font-extrabold px-8 py-5 rounded-full whitespace-nowrap cursor-pointer transition-all duration-200 text-base"
              >
                <span className="w-6 h-6 flex items-center justify-center"><i className="ri-whatsapp-line text-xl"></i></span>
                WhatsApp
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
